import { useContext } from 'react';

import styles from './InstallationDialog.module.css';
import { NAVBAR_ICON_URL } from '@/constants';
import { AppContext } from '@/App.context';
import { Modal } from '@shared/ui/modal/Modal';
import { Share } from './Installation';

/**
 * Moved out of `views/home/Home.tsx` (improvements/07-template-architecture.md's
 * `views/` sort, user directive 2026-08-16: "if code is shared make it one
 * level up and import it") -- confirmed shared across 7+ templates plus
 * `poets` itself, not poets-exclusive like the rest of `Home.tsx` was.
 *
 * Note: this dialog's CSS (`InstallationDialog.module.css`, extracted
 * verbatim from `views/home/Home.module.css`) hardcodes poets' own brand
 * fonts (`Domine-Regular`, `CrimsonText-Regular`). That's not a regression
 * introduced by this move -- every non-poets template already rendered this
 * exact dialog with this exact CSS before the move, since they all imported
 * the same component from `views/home/Home.tsx`. Flagged here as a
 * pre-existing cross-tenant styling leak, not fixed as part of this move.
 */
export const InstallationDialog = ({
	show,
	setShowInstallation,
}: InstallationDialogProps) => {
	const { device } = useContext(AppContext);
	const message = device?.isTouchable ? `Add to Home Screen` : `Add to Dock`;
	return (
		<Modal
			classNames={[styles['installation-dialog-container']]}
			hasCloseBtn={true}
			isOpen={show}
			onClose={() => {
				setShowInstallation(false);
			}}
		>
			<div className={styles['installation-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Install App</h3>
				<p>
					Install this app for quick and easy access <br /> when
					you&apos;re on the go.
				</p>
				{isSafari() ? (
					<ol>
						<li>
							Tap the <Share /> button on your screen.
						</li>
						<li>
							Select <b>&lsquo;{message}&rsquo;</b>.
						</li>
						<li>
							Tap <b>&lsquo;Add&rsquo;</b>.
						</li>
					</ol>
				) : (
					<ol>
						<li>
							Tap the Menu icon (three vertical dots)
							<br /> in the top-right corner of your screen.
						</li>
						<li>
							Select <b>&lsquo;Add to Home screen&rsquo;</b>.
						</li>
						<li>
							Press <b>&lsquo;Install&rsquo;</b>.
						</li>
					</ol>
				)}
			</div>
		</Modal>
	);
};

export interface InstallationDialogProps {
	show: boolean;
	setShowInstallation: (l: boolean) => void;
}

export function isSafari() {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
